/**
 * Boolean Column Pattern
 * 
 * Renders true/false values as checkmarks, toggles, yes/no chips, or custom representations.
 */

import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { StatusBadge } from '@etherisc/ui-kit';
import { TABLE_SETTINGS } from '../../constants/tableSettings';

export type BooleanDisplayType = 'checkmark' | 'toggle' | 'yesno' | 'badge' | 'custom';

export interface BooleanColumnOptions {
  header?: string;
  sortable?: boolean;
  resizable?: boolean;
  width?: number;
  maxWidth?: number;
  minWidth?: number;
  
  // Display options
  displayType?: BooleanDisplayType;
  
  // Checkmark options
  checkmarkStyle?: 'check' | 'checkCircle' | 'simple';
  
  // Badge options
  trueBadgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  falseBadgeVariant?: 'default' | 'secondary' | 'destructive' | 'outline';
  
  // Custom labels
  trueLabel?: string;
  falseLabel?: string;
  nullLabel?: string;
  
  // Toggle functionality (for editable mode)
  editable?: boolean;
  onToggle?: (value: boolean, rowData: unknown) => void;
  
  // Styling
  className?: string;
}

/**
 * Creates a boolean column with various display options
 * 
 * @param accessor - The data accessor key
 * @param options - Optional customization options
 * @returns ColumnDef for TanStack Table
 * 
 * @example
 * // Simple checkmark display
 * booleanColumn('isActive')
 * 
 * // Yes/No chips
 * booleanColumn('isVerified', { 
 *   displayType: 'yesno' 
 * })
 * 
 * // Custom badges
 * booleanColumn('isPremium', {
 *   displayType: 'badge',
 *   trueLabel: 'Premium',
 *   falseLabel: 'Standard',
 *   trueBadgeVariant: 'default',
 *   falseBadgeVariant: 'outline'
 * })
 */
export function booleanColumn<T>(
  accessor: keyof T,
  options: BooleanColumnOptions = {}
): ColumnDef<T> {
  const {
    header = String(accessor),
    sortable = true,
    resizable = true,
    displayType = TABLE_SETTINGS.boolean.displayType,
    checkmarkStyle = TABLE_SETTINGS.boolean.checkmarkStyle,
    trueBadgeVariant = TABLE_SETTINGS.boolean.trueBadgeVariant,
    falseBadgeVariant = TABLE_SETTINGS.boolean.falseBadgeVariant,
    trueLabel = TABLE_SETTINGS.boolean.trueLabel,
    falseLabel = TABLE_SETTINGS.boolean.falseLabel,
    nullLabel = TABLE_SETTINGS.text.emptyDisplay,
    editable = false,
    onToggle,
    width,
    maxWidth,
    minWidth,
  } = options;

  return {
    id: String(accessor),
    header,
    accessorKey: accessor,
    enableSorting: sortable,
    enableResizing: resizable,
    size: width,
    maxSize: maxWidth,
    minSize: minWidth,
    cell: ({ row }) => {
      const value = row.original[accessor] as boolean;
      
      // Handle null/undefined values
      if (value == null || value === undefined) {
        return nullLabel;
      }
      
      // Convert to boolean if needed
      const boolValue = Boolean(value);
      
      switch (displayType) {
        case 'checkmark':
          return renderCheckmark(boolValue, checkmarkStyle);
          
        case 'toggle':
          return renderToggle(boolValue, editable, onToggle);
          
        case 'yesno':
          return boolValue ? trueLabel : falseLabel;
          
        case 'badge':
          return renderBadge(boolValue, trueLabel, falseLabel, trueBadgeVariant, falseBadgeVariant);
          
        case 'custom':
          return boolValue ? trueLabel : falseLabel;
          
        default:
          return boolValue ? '✓' : '✗';
      }
    },
  };
}

/**
 * Renders checkmark icons based on style
 */
function renderCheckmark(value: boolean, style: string): string {
  if (!value) {
    return style === 'simple' ? '✗' : '✗';
  }
  
  switch (style) {
    case 'check':
      return '✓';
    case 'checkCircle':
      return '✅';
    case 'simple':
      return '✓';
    default:
      return '✓';
  }
}

/**
 * Renders toggle switch representation
 */
function renderToggle(value: boolean, editable: boolean, onToggle?: (value: boolean, rowData: unknown) => void) {
  const label = value ? 'ON' : 'OFF';
  const className = `px-2 py-1 rounded text-xs font-medium ${
    value ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
  }`;
  
  if (editable && onToggle) {
    // For interactive toggles, we'd need to implement click handlers
    // For now, just show the visual representation
    return React.createElement('span', { className }, label);
  }
  
  return React.createElement('span', { className }, label);
}

/**
 * Renders badge representation using StatusBadge from ui-kit
 */
function renderBadge(value: boolean, trueLabel: string, falseLabel: string, trueVariant: string, falseVariant: string) {
  const label = value ? trueLabel : falseLabel;
  const variant = value ? trueVariant : falseVariant;
  
  // Map badge variants to StatusBadge variants (using researched API)
  const statusVariantMap: Record<string, 'success' | 'error' | 'warning' | 'info' | 'pending' | 'neutral'> = {
    default: value ? 'success' : 'error',
    secondary: 'neutral', 
    destructive: 'error',
    outline: 'neutral'
  };
  
  const statusVariant = statusVariantMap[variant] || statusVariantMap.default;
  
  // Use StatusBadge from ui-kit - the reusable component with proper API!
  return React.createElement(StatusBadge, { variant: statusVariant, children: label });
}

/**
 * Creates an active/inactive status column
 */
export function activeColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return booleanColumn(accessor, {
    header,
    displayType: 'badge',
    trueLabel: 'Active',
    falseLabel: 'Inactive',
    trueBadgeVariant: 'default',
    falseBadgeVariant: 'secondary',
  });
}

/**
 * Creates a verified/unverified status column
 */
export function verifiedColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return booleanColumn(accessor, {
    header,
    displayType: 'checkmark',
    checkmarkStyle: 'checkCircle',
  });
}

/**
 * Creates a yes/no column
 */
export function yesNoColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return booleanColumn(accessor, {
    header,
    displayType: 'yesno',
  });
}

/**
 * Creates an enabled/disabled toggle column
 */
export function enabledColumn<T>(accessor: keyof T, header?: string): ColumnDef<T> {
  return booleanColumn(accessor, {
    header,
    displayType: 'toggle',
  });
} 